import React from "react";

import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { CardVariant, CardView } from "common/views/card/Card.view";
import { DropdownSelectVariant } from "common/views/dropdownSelect/DropdownSelect.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import HeadingView, { HeadingVariant } from "common/views/heading/Heading.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import { Query } from "core/api/typedQuery";
import DateLabel from "core/components/label/DateLabel";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { isEmpty } from "core/util/ArrayUtils";
import { validateList } from "core/util/ParamUtil";
import StringUtil from "core/util/StringUtil";
import { iDef, nDef } from "core/util/TypeUtils";
import { IOrganizationUserListParams, Organization } from "./entities/OrganizationUserList";
import { User } from "./entities/User";
import { UserListConfig } from "./UserListConfig";
import labelProvider from './UserList.labels';

/**
 * @description displays a list of users in an organization.
 * @author chills
 */

const UsersQuery = Query.queryOne<Organization, IOrganizationUserListParams>(Organization);
const { Labels } = labelProvider;
const sortOptions = [
	{value: "firstName", label: <Labels.FirstName/>},
	{value: "lastName", label: <Labels.LastName />},
	{value: "email", label: <Labels.Email />},
	{value: "-lastLoginDate", label: <Labels.LastLogin />}
];

export const PARAM_USER_ID = "organizaton.user.id";
const PARAM_ORG_GUID = "guid";
const PARAM_SORT = "users.sort";
const PARAM_PAGE_SIZE = "users.pageSize";

@Panel({id: "organization-user-list", configClass: UserListConfig, pageParams: true,labelProvider})
export class UserListPanel extends React.Component<IPanelProps<UserListConfig>> {

	public render() {
		const { config, pageParams } = this.props;

		const guid = pageParams[PARAM_ORG_GUID];

		if (nDef(guid)) {
			return (
				<NonIdealState
					title={<Labels.OrganizationEmpty />}
					description={<Labels.DescriptionEmpty />}
				/>
			);
		}

		const defaultPageSize = iDef(config.pageSize) ? config.pageSize : 10;
		const pageSize = StringUtil.toInteger(pageParams[PARAM_PAGE_SIZE], defaultPageSize);

		const params = {
			guid,
			pageSize,
			sort: validateList(['firstName', 'lastName', 'email'], pageParams[PARAM_SORT], null)
		};

		return [
			config.enableRefine && (
				<BlockView key="refine" variant={BlockVariant.REFINE} viewKey="refine">
					<OptionDropdownSelect
						options={sortOptions}
						value={pageParams[PARAM_SORT]}
						onChange={this.onChangeParam(PARAM_SORT)}
						variant={DropdownSelectVariant.MINIMAL}
						viewKey="refine.sort"
					>
						<Labels.Sort />
					</OptionDropdownSelect>
				</BlockView>
			),
			<UsersQuery key="query" params={params}>
				{({data, loading}) => {
					if (loading) {
						return <Loading />;
					}

					if (!data) {
						return (
							<NonIdealState title="Invalid Organization" description="Unable to locate selected organization." />
						);
					}

					if (isEmpty(data.userList)) {
						return (
							<NonIdealState title="No Users Found" description="Try different search parameters!" />
						);
					}

					return (
						<div key="data">
							{data.userList.map(this.renderUser)}
							{
								config.enableLoadMore && data.userList.length >= pageSize && (
									<BlockView viewKey="paging">
										<ButtonView
											onClick={() => this.onChangeParam(PARAM_PAGE_SIZE)(pageSize + defaultPageSize)}
											variant={ButtonVariant.PRIMARY}
											viewKey="load-more"
										>
											<Labels.LoadMore />
										</ButtonView>
									</BlockView>
								)
							}
						</div>

					);
				}}
			</UsersQuery>
		];
	}

	private renderUser = (user: User) => (
		<PageLinkView params={{[PARAM_USER_ID]: user.id}} key={user.id} viewKey="user-link">
			<CardView interactive={true} variant={CardVariant.SURFACE} viewKey="card">
				<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="card-content">
					<div style={{display: 'flex'}}>
						<div style={{flexGrow: 1}}>
							<HeadingView children={user.firstName + ' ' + user.lastName} viewKey="name" />
							{user.email}
						</div>
						{
							user.lastLoginDate && (
								<div>
									<Labels.LastLogin />
									{": "}
									<DateLabel value={user.lastLoginDate} />
								</div>
							)
						}
					</div>
				</BlockView>
			</CardView>
		</PageLinkView>
	)

	private onChangeParam = (name: string) => (value: any) => {
		this.props.actions.pushPageParam(name, value);
	}
}



// WEBPACK FOOTER //
// ./src/panels/organizationAdmin/UserList.panel.tsx